import React from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FONTS } from "../../../constants/styles";

// Shared visual language for the Analytics section. Reserve the serif
// (`HeroFigure`) strictly for MONEY figures so it keeps its meaning; counts and
// other heroes use DM Sans. Every metric that pairs a label with a value uses
// `StatementRow`, which wraps the label and pins the value — RU-safe by design.

export const SECTION_LABEL_SX = {
    fontSize: "11px",
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
} as const;

export const formatHours = (minutes: number): string => {
    const hours = minutes / 60;
    return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
};

/** The signature — a big editorial money figure (Fraunces, tabular). Money only. */
export const HeroFigure = ({
    children,
    color,
    size = 52,
}: {
    children: React.ReactNode;
    color: string;
    size?: number;
}) => (
    <Typography
        className="no-translation"
        fontFamily={FONTS.serif}
        fontWeight={500}
        color={color}
        lineHeight={1}
        sx={{
            fontSize: `${size}px`,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.015em",
        }}
    >
        {children}
    </Typography>
);

/** One statement line: label wraps freely on the left, value pinned right and
 *  never wrapping. This is what makes the whole section RU-safe. */
export const StatementRow = ({
    label,
    value,
    translateValue = false,
}: {
    label: string;
    value: string;
    translateValue?: boolean;
}) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}
        sx={{
            py: 0.9,
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            "&:last-of-type": { borderBottom: "none", pb: 0 },
        }}
    >
        <Typography fontSize="13px" color="text.secondary" sx={{ minWidth: 0 }}>
            {label}
        </Typography>
        <Typography
            className={translateValue ? undefined : "no-translation"}
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="16px"
            noWrap
            sx={{ flex: "0 0 auto" }}
        >
            {value}
        </Typography>
    </Stack>
);

/** Tinted +/- delta pill (green up, red down) with an optional note. */
export const DeltaPill = ({
    deltaPct,
    note,
}: {
    deltaPct: number;
    note?: string;
}) => {
    const theme = useTheme();
    const up = deltaPct >= 0;
    return (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="baseline"
            sx={{
                bgcolor: alpha(
                    up ? theme.palette.success.main : theme.palette.error.main,
                    0.12
                ),
                borderRadius: "10px",
                px: 1,
                py: 0.35,
                flex: "0 0 auto",
            }}
        >
            <Typography
                className="no-translation"
                fontSize="12px"
                fontWeight={700}
                color={up ? "success.main" : "error.main"}
            >
                {up ? "+" : ""}
                {Math.round(deltaPct)}%
            </Typography>
            {note && (
                <Typography fontSize="11px" color="text.secondary" noWrap>
                    {note}
                </Typography>
            )}
        </Stack>
    );
};

/** Progress meter — bar capped at 100% even when the real value exceeds it. */
export const Meter = ({ pct, color }: { pct: number; color: string }) => (
    <Box
        sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: alpha(color, 0.1),
            overflow: "hidden",
        }}
    >
        <Box
            sx={{
                height: "100%",
                width: `${Math.min(100, Math.max(0, pct))}%`,
                bgcolor: color,
                borderRadius: 4,
                transition: "width 0.4s ease",
            }}
        />
    </Box>
);

/** Small sort toggle pill. */
export const SortPill = ({
    label,
    active,
    onClick,
    color,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
    color: string;
}) => (
    <ButtonBase
        onClick={onClick}
        sx={{
            borderRadius: "10px",
            px: 1.25,
            py: 0.5,
            bgcolor: active ? color : alpha(color, 0.08),
            flex: "0 0 auto",
        }}
    >
        <Typography
            fontFamily={FONTS.display}
            fontWeight={600}
            fontSize="12px"
            color={active ? "white" : color}
        >
            {label}
        </Typography>
    </ButtonBase>
);

/** Ranked list row: rank · name (ellipsis) · primary money value · optional sub. */
export const RankedRow = ({
    index,
    name,
    value,
    sub,
    color,
}: {
    index: number;
    name: string;
    value: string;
    sub?: string;
    color: string;
}) => (
    <Stack direction="row" alignItems="center" spacing={1.25}>
        <Typography
            className="no-translation"
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="12px"
            color="text.disabled"
            sx={{ width: 14, flex: "0 0 auto", textAlign: "right" }}
        >
            {index}
        </Typography>
        <Typography
            fontSize="14px"
            fontWeight={500}
            flex={1}
            minWidth={0}
            sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {name}
        </Typography>
        <Typography
            className="no-translation"
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="14px"
            color={color}
            sx={{ flex: "0 0 auto" }}
        >
            {value}
        </Typography>
        {sub && (
            <Typography
                className="no-translation"
                fontSize="12px"
                color="text.secondary"
                sx={{ minWidth: 30, textAlign: "right", flex: "0 0 auto" }}
            >
                {sub}
            </Typography>
        )}
    </Stack>
);
